import { IPromise } from 'angular';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  CompactInlineAlertWidget,
  DialogPositioningBehavior,
  DialogType,
  InlineAlertType,
  ButtonWidget,
  List,
  ListItem,
} from '@tableau/widgets';

import { Messages, Formatters } from '../../../codegen/Localize';
import { Location } from '../../../common/react/Location';
import { ButtonWithSpinner } from '../../../common/react/components/ButtonWithSpinner';
import { SparseCachedList } from '../../../common/react/SparseCachedList';
import { Order } from '../../../ts/helpers/Order';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { OrderKeys } from '../../../ts/models/OrderKeys';
import { ReactModalService } from '../../../ts/services/ReactModalService';
import { delayPromise } from '../../utils/Delay';
import { AddRemoveCollectionItemsData, CollectionContentItem } from './CollectionModels';
import { CollectionsResourceQuery, CollectionItemsResourceQuery, ItemsResourceQuery } from './Collections';
import { CollectionsDialogStyles } from './CollectionsDialogStyles';
import { ICollectionInfo } from './ICollectionInfo';
import { SiteUrls } from '@tableau/shared-urls';
import { SpaceInfo } from '../../state/SpaceInfo';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { CreateEmptyCollectionAction, createEmptyCollectionAction } from '../../actions/spaces/ContentExplorerCreateActions';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { ManageItemsTelemetry, sendManageItemsEvent } from './CollectionsTelemetry';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { L10n } from '../../../ts/L10n/L10n';
import { showActionableToast } from '../../../common/react/components/ActionableMessage';
import { CollectionsErrorCode } from '../../../ts/helpers/ErrorCodes';
import { classes } from '../../../style';

export const ManageItemsInCollectionsDialogTestId = 'add-to-collections-dialog';
export const ManageItemsInCollectionsCreateButtonTestId = `${ManageItemsInCollectionsDialogTestId}-create`;
export const ManageItemsInCollectionsCancelButtonTestId = `${ManageItemsInCollectionsDialogTestId}-cancel`;
export const ManageItemsInCollectionsSaveButtonTestId = `${ManageItemsInCollectionsDialogTestId}-save`;
export const ManageItemsInCollectionsListContainerTestId = `${ManageItemsInCollectionsDialogTestId}-list-container`;
export const ManageItemsInCollectionsErrorTestId = `${ManageItemsInCollectionsDialogTestId}-error`;
export const ManageItemsInCollectionsSuccessToastTestId = `${ManageItemsInCollectionsDialogTestId}-success`;
export const ManageItemsInCollectionsErrorToastTestId = `${ManageItemsInCollectionsDialogTestId}-error`;

export namespace ManageItemsInCollectionsDialog {
  export interface Options {
    readonly items: IContentInfo[];
    readonly targetCollectionLuids?: string[];
    readonly userId: string;
    readonly isAdmin: boolean;
    readonly siteUrls: SiteUrls;
    readonly spaceInfo: SpaceInfo;
    readonly manageItemsTelemetry: ManageItemsTelemetry;
  }

  interface ContentsDispatchProps {
    createEmptyCollection: (itemsToPossiblyAdd?: IContentInfo[], manageItemsTelemetry?: ManageItemsTelemetry) => CreateEmptyCollectionAction;
  }

  export interface ContentsProps extends Options, ContentsDispatchProps {
    readonly closeDialog: () => void;
  }

  export interface ContentsState {
    readonly busy: boolean;
    readonly error: string;
    readonly collections: ICollectionInfo[];
    readonly collectionsToUpdate: Set<string>;
  }

  export const CollectionsBlockSize = 1000;

  export function buildCollectionLuidToHasItemsMap(
    collectionItems: CollectionContentItem[],
    numberOfItems: number
  ): Map<string, boolean> {
    const collectionLuidToHasItemsMap = new Map<string, boolean>();

    // map collection luid to count of collectionItems
    // and if it matches numberOfItems it means
    // it contains all items were trying to add
    const counts = _.countBy(collectionItems, 'collectionLuid');

    _.each(counts, (count, collectionLuid) => {
      if (count === numberOfItems) {
        collectionLuidToHasItemsMap.set(collectionLuid, true);
      }
    });

    return collectionLuidToHasItemsMap;
  }

  export class Contents extends React.PureComponent<ContentsProps, ContentsState> {
    private collectionsCache: SparseCachedList<ICollectionInfo, ICollectionInfo>;
    private listRef: React.RefObject<List> = React.createRef();
    private isUnmounted = false;

    public constructor(props: ContentsProps) {
      super(props);

      this.state = {
        busy: false,
        error: undefined,
        collections: undefined, // undefined means not-yet-fetched
        collectionsToUpdate: new Set<string>(),
      };
    }

    public componentDidMount() {
      this.updateCollectionsCacheWithNewQuery();
    }

    public componentDidUpdate(prevProps: ContentsProps, prevState: ContentsState) {
      if (prevProps.isAdmin !== this.props.isAdmin) {
        this.updateCollectionsCacheWithNewQuery();
      }

      if (!prevState.collections && this.state.collections) {
        // focus first collection after collections have been fetched
        const listDom = ReactDOM.findDOMNode(this.listRef.current) as HTMLDivElement;
        const firstListElement = listDom?.querySelector<HTMLElement>(`[role="option"]`);
        firstListElement?.focus();
      }
    }

    public componentWillUnmount() {
      this.isUnmounted = true;
    }

    private async updateCollectionsCacheWithNewQuery() {
      try{
        // clear existing collections
        !this.isUnmounted && this.setState({
          collections: undefined,
        });

        const filters = !this.props.isAdmin ? [ ServerFilterClauses.serverFilterClauses.owner(this.props.userId) ] : [];
        const query = new CollectionsResourceQuery()
          .withOrder([Order.desc(OrderKeys.updatedAt)])
          .withFilters(filters);
        this.collectionsCache = new SparseCachedList<ICollectionInfo, ICollectionInfo>(query, _.identity, CollectionsBlockSize);

        // fetch the first block of the cache after initialization
        await this.fetchCollections(0, CollectionsBlockSize);
      } catch (e) {
        !this.isUnmounted && this.setState({ error: Messages.collectionsFailedToLoad() });
      }
    }

    private handleScroll = async (e: React.UIEvent) => {
      const scrollTop = e.currentTarget.scrollTop;
      const clientHeight = e.currentTarget.clientHeight;
      const startIndex = Math.floor(scrollTop / CollectionsDialogStyles.COLLECTION_ITEM_SCROLL_HEIGHT);
      const itemsToFetch = Math.ceil(clientHeight / CollectionsDialogStyles.COLLECTION_ITEM_SCROLL_HEIGHT);
      const endIndex = startIndex + itemsToFetch;
      await this.fetchCollections(startIndex, endIndex);
    }

    public render(): JSX.Element {
      return (
        <>
          <div className={CollectionsDialogStyles.ItemsToAdd}>{this.getItemsToAddMessage()}</div>
          {this.getCollectionsListElement()}

          <div className={!this.state.error ? CollectionsDialogStyles.ActionButtonsFlexContainer : ''}>
            {!this.state.error && <ButtonWidget
                title={Messages.newCollection()}
                buttonType={ButtonType.LowEmphasis}
                testId={ManageItemsInCollectionsCreateButtonTestId}
                handleClick={this.handleCreateCollectionClicked}
                className={CollectionsDialogStyles.CreateCollectionButton}
              >
                <img src={imgPath('plus_14px-small.svg')} role={'presentation'}/><span>{' ' + Messages.newCollection()}</span>
              </ButtonWidget>
            }
            <div className={CollectionsDialogStyles.CreateCancelButtons}>
              <ButtonWithSpinner
                title={Messages.cancel()}
                buttonType={ButtonType.Outline}
                clicked={this.props.closeDialog}
                testId={ManageItemsInCollectionsCancelButtonTestId}
                disabled={this.state.busy}
              />
              <span className="tb-margin-left-wide">
                <ButtonWithSpinner
                  title={Messages.add()}
                  buttonType={ButtonType.Primary}
                  clicked={this.saveChanges}
                  testId={ManageItemsInCollectionsSaveButtonTestId}
                  disabled={this.state.busy || !!this.state.error || this.state.collectionsToUpdate.size === 0}
                  showSpinner={this.state.busy}
                />
              </span>
            </div>
          </div>
        </>
      );
    }

    private getItemsToAddMessage(): string {
      if (this.props.items.length > 1) {
        return Messages.addMultipleItemsToCollection({ ITEM_COUNT: String(this.props.items.length) });
      } else if (this.props.items.length === 1) {
        return Messages.addSingleItemToCollection({ ITEM_NAME: this.props.items[0].name });
      }
      return undefined;
    }

    private async fetchCollections(startIndex: number, endIndex: number): Promise<void> {
      try {
        const collections = await this.collectionsCache.getItems(startIndex, endIndex - 1);
        const targetCollectionLuids = this.props.targetCollectionLuids ?? [];

        !this.isUnmounted && this.setState({ collections }, () => {
          this.props.manageItemsTelemetry.numCollectionsShown = collections.length;
        });

        // If the given collection is specified as a "target collection" (e.g., coming from the
        // 'Create Collection' dialog), start it off as checked and add to `collectionsToUpdate`.
        targetCollectionLuids.forEach((collectionLuid) => {
          this.updateItemIsInCollectionMap(collectionLuid, true);
        });
      } catch (e) {
        !this.isUnmounted && this.setState({ error: Messages.collectionsFailedToLoad() });
      }
    }

    private getCollectionsListElement(): JSX.Element | JSX.Element[] {
      if (this.state.error) {
        return (
          <CompactInlineAlertWidget
            testId={ManageItemsInCollectionsErrorTestId}
            type={InlineAlertType.Error}
          >
            {this.state.error}
          </CompactInlineAlertWidget>
        );
      } else if (!this.state.collections) {
        return (
          <div className={CollectionsDialogStyles.Spinner} >
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Small}
              testId="collections-list-spinner" />
          </div>
        );
      }

      const items: ListItem[] = this.state.collections.reduce((filtered, collection): ListItem[] => {
        // collections cache can be sparse, filter out unfetched collections
        // using `reduce` to only traverse list of collections once (could be very long)
        if(collection) {
          filtered.push({
            value: collection.luid,
            content: this.getCollectionListItemContent(collection),
            testId: `${collection.name}-collection-list-item`
          });
        }
        return filtered;
      }, []);

      return <List
        items={items}
        className={CollectionsDialogStyles.CollectionsList}
        onSelect={this.updateItemIsInCollectionMap}
        onScroll={this.handleScroll}
        ref={this.listRef}
        testId={ManageItemsInCollectionsListContainerTestId}
      />;
    }

    private getCollectionListItemContent = (collection: ICollectionInfo): JSX.Element => {
      const checked = this.state.collectionsToUpdate.has(collection.luid);
      const isNewlyCreated = this.props.targetCollectionLuids?.includes(collection.luid);

      return <div className={CollectionsDialogStyles.CollectionListItemContainer}>
        {checked && <img className={CollectionsDialogStyles.CollectionListItemCheckmark} src={imgPath('check_12px-xsmall.svg')}/>}
        <span className={classes(CollectionsDialogStyles.CollectionListItemText, { unchecked: !checked })}>{collection.name}</span>
        {isNewlyCreated && <NewBadge/>}
      </div>;
    };

    private saveChanges = async () => {
      const promises: Promise<AddRemoveCollectionItemsData>[] = [];
      this.state.collectionsToUpdate.forEach(luid => {
        promises.push(addItemsToCollection(luid, this.props.items));
      });

      this.setState({ busy: true });
      if (this.isUnmounted) {
        return;
      }

      try {
        // `Promise.all` rejects as soon as one of the promises rejects. To process all add item results even if one rejects,
        // map rejecting promises to their errors. Then, filter responses into add items responses and thrown errors.
        const results = await Promise.all(promises.map(p => p.catch(e => e)));
        const addItemsResponses: AddRemoveCollectionItemsData[] = results.filter((result) => isAddItemsResponse(result));
        const errored = results.filter((result) => !isAddItemsResponse(result));

        this.handleAddItemsResponses(addItemsResponses, errored);
      } catch {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.unexpectedError()
        });
      } finally {
        !this.isUnmounted && this.setState({ busy: false });
        await delayPromise(100); // Delay a short amount so that any action toast will appear before the dialog closes. It looks better this way.

        const collectionLuids = this.state.collections.map((c) => c.luid);
        const containsNewCollections = _.intersection(this.props.targetCollectionLuids || [], collectionLuids).length > 0;
        this.props.manageItemsTelemetry.itemsAddedToNewCollections = containsNewCollections;
        this.props.manageItemsTelemetry.numCollectionsAddedTo = this.state.collectionsToUpdate.size;
        this.props.manageItemsTelemetry.result = 'ok';
        sendManageItemsEvent(this.props.manageItemsTelemetry, this.props.spaceInfo);

        this.props.closeDialog();
      }
    }

    private updateItemIsInCollectionMap = (luid: string, checked?: boolean) => {
      if(_.isUndefined(luid) || _.isEmpty(luid)) {
        return;
      }

      const included = this.state.collectionsToUpdate.has(luid);
      const collectionsToUpdate = new Set(this.state.collectionsToUpdate);
      if(!included || checked){
        collectionsToUpdate.add(luid);
      } else {
        collectionsToUpdate.delete(luid);
      }

      !this.isUnmounted && this.setState({ collectionsToUpdate });
    }

    private handleAddItemsResponses = (addItemsResponses: AddRemoveCollectionItemsData[], errored: any[]) => {
      // There are two types of errors. `errored` contains collection-level errors (e.g. user unauthorized to add to collection).
      // `addItemsResponses` could still contain item-level errors (e.g. unable to add 1 item out of 3 to collection), so go
      // through `addItemsResponses` to find successful add results.
      const successfulResults: AddRemoveCollectionItemsData[] = [];
      addItemsResponses.forEach((result) => {
        // if all items are added or already in the collection, count as a successful result
        if(result.numItemsSuccessfullyUpdated === this.props.items.length) {
          successfulResults.push(result);
        } else {
          // swallow DuplicateKey errors
          const duplicateKeys = result.errors.filter((error) => error.errorCode === CollectionsErrorCode.DUPLICATE_KEY);
          if(duplicateKeys.length === this.props.items.length - result.numItemsSuccessfullyUpdated) {
            successfulResults.push(result);
          }
        }
      });

      const countCollectionsSuccessful = successfulResults.length;
      const countCollectionsFailed = addItemsResponses.length - countCollectionsSuccessful;

      if(countCollectionsSuccessful > 0) {
        showActionableToast({
          testId: ManageItemsInCollectionsSuccessToastTestId,
          message: this.getSuccessMessage(countCollectionsSuccessful),
          actionMessage: countCollectionsSuccessful === 1 ? Messages.goToCollection() : Messages.goToCollections(),
          action: () => { countCollectionsSuccessful === 1
            ? Location.instance.setUrl(this.props.siteUrls.collectionUrl(successfulResults[0].collectionLuid))
            : Location.instance.setUrl(this.props.siteUrls.collectionsUrl());
          }
        }, ToastType.Success);
      }

      if(countCollectionsFailed > 0 || errored.length > 0){
        const count = countCollectionsFailed + errored.length;
        const toaster = ToasterService.instance;
        toaster.pop({
          type: ToastType.Error,
          text: Messages.collectionsAddItemsToCollectionsError({
            COLLECTION_COUNT: count,
            COLLECTION_COUNT_LOC: Formatters.formatCount(count)
          }),
          testId: ManageItemsInCollectionsErrorToastTestId
        });
      }
    }

    private getSuccessMessage = (successCount: number): string => {
      let successMessage;
      if(this.props.items.length === 1) {
        // if only attempting to add 1 item, can supply content name in message
        const name = this.props.items[0].name;
        successMessage = successCount === 1
          ? Messages.collectionsAddItemToOneCollectionSuccess({ CONTENT_NAME: L10n.elideIfLong(name) })
          : Messages.collectionsAddItemToMultCollectionsSuccess({
            CONTENT_NAME: L10n.elideIfLong(name),
            COLLECTIONS_COUNT: L10n.localizedCount(successCount)
          });
      } else {
        successMessage = successCount === 1
          ? Messages.collectionsAddItemsToOneCollectionSuccess({CONTENT_COUNT: L10n.localizedCount(this.props.items.length)})
          : Messages.collectionsAddItemsToMultCollectionsSuccess({
            CONTENT_COUNT: L10n.localizedCount(this.props.items.length),
            COLLECTIONS_COUNT: L10n.localizedCount(successCount)
          });
      }
      return successMessage;
    }

    private handleCreateCollectionClicked = () => {
      this.props.manageItemsTelemetry.numCollectionsCreatedAttempts = this.props.manageItemsTelemetry.numCollectionsCreatedAttempts + 1;
      this.props.manageItemsTelemetry.creatingCollection = true;
      this.props.createEmptyCollection(this.props.items, this.props.manageItemsTelemetry);
      this.props.closeDialog();
    };
  }

  const isAddItemsResponse = (result: any) => {
    return result.hasOwnProperty('numItemsSuccessfullyUpdated');
  };

  const mapContentsDispatchProps: MapDispatchToProps<ContentsDispatchProps> = dispatch => bindActionCreators({
    createEmptyCollection: createEmptyCollectionAction,
  }, dispatch);

  export const ConnectedContents = connect(null, mapContentsDispatchProps)(Contents);

  async function addItemsToCollection(luid: string, items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    const query = new CollectionItemsResourceQuery(luid);
    return query.addItemsToCollection(items);
  }

  export function open(opts: Options): IPromise<any> {
    const closeDialog = () => {
      if (opts.manageItemsTelemetry.result !== 'ok' && !opts.manageItemsTelemetry.creatingCollection) {
        opts.manageItemsTelemetry.result = 'cancel';
        sendManageItemsEvent(opts.manageItemsTelemetry, opts.spaceInfo);
      }
      modalInstance && modalInstance.close();
    };
    opts.manageItemsTelemetry.creatingCollection = false;

    const modalInstance = ReactModalService.open({
      title: Messages.addToCollectionsDialogTitle(),
      testId: ManageItemsInCollectionsDialogTestId,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      dismissDialogOnCloseAction: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH + 'px' },
      dialogContent: <ConnectedContents {...opts} closeDialog={closeDialog} />,
    });
    return modalInstance.result;
  }
}

export function NewBadge(): JSX.Element {
  return <span className={CollectionsDialogStyles.NewBadge}>
    <span style={{marginLeft: 8, marginRight: 8}}>{Messages.newBadge()}</span>
  </span>;
}